import React, { useState } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Button
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import {
  useGetKeywords,
  useToggleKeywordActive,
  useAddKeyword
} from '@hooks/useKeywords';

const EditKeywords = ({ pageDealId }: { pageDealId: number }) => {
  const { data: keywords, isLoading } = useGetKeywords(pageDealId);
  const { mutateAsync: toggleKeywordActive } = useToggleKeywordActive();
  const { mutateAsync: addKeyword } = useAddKeyword();

  const [newKeyword, setNewKeyword] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleToggle = async (id: number, isActive: boolean) => {
    await toggleKeywordActive({ id, isActive: !isActive, pageDealId });
  };

  const handleAddKeyword = async () => {
    if (newKeyword.trim() === '') return;
    setIsAdding(true);
    try {
      await addKeyword({ keyword: newKeyword, pageDealId });
      setNewKeyword(''); // Clear the input
    } catch (error) {
      console.error('Failed to add keyword:', error);
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth="600px" margin="0 auto">
      <Typography variant="h6" gutterBottom>
        Keywords
      </Typography>
      <Box display="flex" gap={2} alignItems="center" mb={3}>
        <TextField
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          placeholder="Add a new keyword"
          variant="outlined"
          size="small"
          fullWidth
        />
        <Button
          onClick={handleAddKeyword}
          variant="outlined"
          color="primary"
          disabled={isAdding}
        >
          {isAdding ? 'Adding...' : 'Add'}
        </Button>
      </Box>
      {(!keywords || keywords.length === 0) && (
        <Typography>No keywords available.</Typography>
      )}
      <List>
        {keywords.map((keyword: any) => (
          <ListItem
            key={keyword.id}
            sx={{
              borderBottom: '1px solid #e0e0e0',
              paddingY: 1.5
            }}
            secondaryAction={
              <Tooltip
                title={`Toggle active state for "${keyword.keyword}"`}
                arrow
              >
                <IconButton
                  edge="end"
                  onClick={() => handleToggle(keyword.id, keyword.isActive)}
                >
                  {keyword.isActive ? (
                    <StarIcon sx={{ color: '#FFD700' }} />
                  ) : (
                    <StarBorderIcon />
                  )}
                </IconButton>
              </Tooltip>
            }
          >
            <ListItemText
              primary={keyword.keyword}
              secondary={`Clicks: ${keyword.clicks} | Impressions: ${
                keyword.impressions
              } | Avg Position: ${keyword.position} ${
                keyword?.isActive &&
                (keyword.position >= 3 || keyword.position === 0)
                  ? ' Used for SEO Desc'
                  : ''
              }`}
              primaryTypographyProps={{
                fontWeight:
                  keyword?.isActive &&
                  (keyword.position >= 3 || keyword.position === 0)
                    ? 'bold'
                    : 'initial'
              }}
              secondaryTypographyProps={{ color: 'text.secondary' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default EditKeywords;
